import { type KeyboardEvent, useEffect, useRef, useState } from 'react';

type Options<T> = {
    items: T[];
    getKey: (item: T) => string;
    isItemDisabled?: (item: T) => boolean;
    searchTerm: string;
    onSelect: (item: T) => void;
};

export function useSearchCombobox<T>({
    items,
    getKey,
    isItemDisabled,
    searchTerm,
    onSelect,
}: Options<T>) {
    const [isOpenIntent, setIsOpenIntent] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const itemRefs = useRef<Array<HTMLElement | null>>([]);

    const isOpen = isOpenIntent && searchTerm.trim().length > 0;

    // A highlight is an index, so it means nothing once the rows under it are replaced or
    // re-ordered. Keys rather than the array's identity: callers need not memoise `items`.
    const itemKeys = items.map(getKey).join('\n');
    const [highlightedKeys, setHighlightedKeys] = useState(itemKeys);
    if (itemKeys !== highlightedKeys) {
        setHighlightedKeys(itemKeys);
        setHighlightedIndex(-1);
    }

    useEffect(() => {
        if (searchTerm.trim().length > 0) {
            setIsOpenIntent(true);
        }
        setHighlightedIndex(-1);
    }, [searchTerm]);

    useEffect(() => {
        if (highlightedIndex !== -1) {
            itemRefs.current[highlightedIndex]?.focus();
        }
    }, [highlightedIndex]);

    const isDisabledAt = (index: number) => {
        const item = items[index];
        return item === undefined || (isItemDisabled?.(item) ?? false);
    };

    const findEnabledIndex = (from: number, step: 1 | -1) => {
        for (let index = from + step; index >= 0 && index < items.length; index += step) {
            if (!isDisabledAt(index)) return index;
        }
        return -1;
    };

    const selectItem = (item: T) => {
        setIsOpenIntent(false);
        setHighlightedIndex(-1);
        onSelect(item);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setIsOpenIntent(true);
            setHighlightedIndex((current) => {
                const next = findEnabledIndex(current, 1);
                return next === -1 ? current : next;
            });
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex((current) => {
                const previous = findEnabledIndex(current, -1);
                return previous === -1 ? current : previous;
            });
        } else if (event.key === 'Enter') {
            // A search field never submits the form around it, and an unhandled Enter would also
            // reach the dropdown trigger and toggle the results.
            event.preventDefault();
            const highlighted = items[highlightedIndex];
            if (highlighted !== undefined && !isDisabledAt(highlightedIndex)) {
                selectItem(highlighted);
            }
        } else if (event.key === 'Escape') {
            event.preventDefault();
            setIsOpenIntent(false);
        }
    };

    return {
        isOpen,
        setIsOpen: setIsOpenIntent,
        highlightedIndex,
        setHighlightedIndex,
        inputRef,
        itemRefs,
        handleKeyDown,
        selectItem,
    };
}
